import { createContext, memo, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { useMemoizedContextProvider, useMemoizedContextSelector } from '@air/react-memoized-context';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type FreeformAnnotation } from '~/components/Annotations/shared/types';
import {
  type AnnotationContextType,
  type AnnotationContextValue,
  AnnotationProviderActions,
  defaultValue,
} from '~/providers/AnnotationProvider/annotationProviderTypes';
import { annotationsDispatch } from '~/providers/AnnotationProvider/annotationProviderUtils';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type AnnotationProviderProps = {
  children: ReactNode;
};

const AnnotationContext = createContext<AnnotationContextType>(defaultValue);

export const AnnotationProvider = memo(({ children }: AnnotationProviderProps) => {
  const { contextValue } = useMemoizedContextProvider<AnnotationContextValue>(
    {
      activeAnnotation: undefined,
    },
    annotationsDispatch,
  );

  const setActiveAnnotation = useCallback(
    (annotation?: FreeformAnnotation) =>
      contextValue.dispatch({ type: AnnotationProviderActions.setActiveAnnotation, data: annotation }),
    [contextValue],
  );

  const value: AnnotationContextType = useMemo(
    () => ({
      ...contextValue,
      setActiveAnnotation,
    }),
    [contextValue, setActiveAnnotation],
  );

  return <AnnotationContext.Provider value={value}>{children}</AnnotationContext.Provider>;
});

AnnotationProvider.displayName = 'AnnotationProvider';

export const useAnnotationContext = () => {
  const context = useContext(AnnotationContext);

  if (context === defaultValue) {
    const error = 'AnnotationContext used outside of AnnotationProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};

export function useAnnotationContextSelector<T>(selector: (st: AnnotationContextValue) => T) {
  const context = useAnnotationContext();

  return useMemoizedContextSelector(context, selector);
}
